import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@abyss/web/ui/Box';
import { Label } from '@abyss/web/ui/Label';
import { Button } from '@abyss/web/ui/Button';
import { Card } from '@abyss/web/ui/Card';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { Heading } from '@abyss/web/ui/Heading';
import { Divider } from '@abyss/web/ui/Divider';
import { DataTable } from '@abyss/web/ui/DataTable';
import { V2Tooltip } from '@abyss/web/ui/Tooltip';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { useDataTable } from '@abyss/web/hooks/useDataTable';
import { PageTitleService } from '@src/api/common/PageTitleService';
import { BreadcrumbService } from '@src/api/common/BreadcrumbService';
import { API_URL } from '@src/constants/BaseUrls';
import { CookieService } from '@src/api/common/CookieService';
import { RouteToolStepper } from '../Stepper';
import { Breadcrumbs, Title } from './PageInfo';
import { PowerBIMapViewer } from './PowerBIReportPanel';

type TabKey = 'tables' | 'maps';
const UPLOAD_STATE_STORAGE_KEY = 'routeToolUploadState';
const ROUTE_TOOL_MAPS_CACHE_KEY = 'routeToolMapsCache';
const ROUTE_TOOL_UPLOAD_FLAGS_KEY = 'routeToolUploadFlags';

// Module-level helper — usable in both ResultsScreen and DataTableCard inner component.
// Returns Authorization header from the token stored by MsalService after login.
// No credentials mode needed — Bearer token auth does not use browser-managed cookies.
const getAuthHeaders = (): HeadersInit => {
  const token = CookieService.GetCookie('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

interface TableData {
  name: string;
  icon: string;
  path: string;
  columns: string[];
  rows: Record<string, string | number | boolean | null | undefined>[];
  totalRows?: number;
}

type ParsedTable = {
  columns: string[];
  rows: Record<string, string | number | boolean | null | undefined>[];
};

interface EndpointTableConfig {
  name: string;
  icon: string;
  path: string;
  /** Skip this endpoint when the warehouse file was not uploaded. */
  requiresWarehouse?: boolean;
  /** Skip this endpoint when the user file was not uploaded. */
  requiresUser?: boolean;
}

interface MapConfig {
  title: string;
  description: string;
  icon: string;
  path: string;
  /** Skip this map endpoint when the user file was not uploaded. */
  requiresUser?: boolean;
}

const CLUSTER_LABELS: Record<number, string> = {
  '-1': 'Outlier',
  0: 'Group A',
  1: 'Group B',
} as Record<number, string>;

const SITE_CLUSTER_TABLE_NAME = 'Site-Cluster List';
const WAREHOUSE_CLUSTER_TABLE_NAME = 'Warehouse-Cluster Distances';
const OUTLIER_TOOLTIP_MESSAGE =
  "This site is not in proximity to any of the available group of sites so it's defined as an outlier.";

const normalizeKey = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '');

const isClusterColumn = (columnName: string) =>
  normalizeKey(columnName) === 'cluster';

const isDurationColumn = (columnName: string) => {
  const normalized = normalizeKey(columnName);
  return (
    normalized.includes('minute') ||
    normalized.includes('min') ||
    normalized.includes('duration') ||
    normalized.includes('traveltime') ||
    normalized === 'time'
  );
};

const formatDurationFromMinutes = (totalMinutes: number): string => {
  if (!Number.isFinite(totalMinutes)) {
    return '';
  }

  const roundedMinutes = Math.max(0, Math.round(totalMinutes));
  if (roundedMinutes < 60) {
    return `${roundedMinutes} minute${roundedMinutes === 1 ? '' : 's'}`;
  }

  const hours = Math.floor(roundedMinutes / 60);
  const remainingMinutes = roundedMinutes % 60;
  if (remainingMinutes === 0) {
    return `${hours} hr${hours === 1 ? '' : 's'}`;
  }

  return `${hours} hr${hours === 1 ? '' : 's'} ${remainingMinutes} minute${remainingMinutes === 1 ? '' : 's'}`;
};

const resolveDurationUnitFromColumn = (
  columnName: string
): 'hours' | 'minutes' => {
  const normalized = normalizeKey(columnName);
  if (
    normalized.includes('hour') ||
    normalized.endsWith('hr') ||
    normalized.endsWith('hrs')
  ) {
    return 'hours';
  }
  if (normalized.includes('minute') || normalized.includes('min')) {
    return 'minutes';
  }

  // For ambiguous duration/travel time fields, backend values are expected in hours.
  return 'hours';
};

const toDurationLabel = (value: unknown, columnName: string): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'string' && /hr|hour|minute|min/i.test(value)) {
    return value;
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return String(value);
  }

  const unit = resolveDurationUnitFromColumn(columnName);
  const totalMinutes = unit === 'hours' ? numeric * 60 : numeric;
  return formatDurationFromMinutes(totalMinutes);
};

const parseClusterValue = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value);
  }

  const text = String(value ?? '').trim();
  if (!text) {
    return null;
  }

  const normalized = text.toLowerCase();
  if (normalized === 'outlier') {
    return -1;
  }

  const groupMatch = normalized.match(/^group\s*([a-f])$/);
  if (groupMatch) {
    return groupMatch[1].charCodeAt(0) - 'a'.charCodeAt(0);
  }

  const parsed = Number(text);
  if (!Number.isNaN(parsed) && Number.isFinite(parsed)) {
    return Math.trunc(parsed);
  }

  return null;
};

const toClusterLabel = (value: unknown): string => {
  const parsed = parseClusterValue(value);
  if (parsed === null) {
    return String(value ?? '');
  }

  return CLUSTER_LABELS[parsed] || 'Group B';
};

const findPreferredAddressColumn = (columns: string[]): string | null => {
  const normalizedColumns = columns.map((column) => ({
    original: column,
    normalized: normalizeKey(column),
  }));

  const exactMatch = normalizedColumns.find(
    (item) =>
      item.normalized === 'siteaddress' || item.normalized === 'sitefulladdress'
  );
  if (exactMatch) {
    return exactMatch.original;
  }

  const containsSiteAndAddress = normalizedColumns.find(
    (item) =>
      item.normalized.includes('site') && item.normalized.includes('address')
  );
  if (containsSiteAndAddress) {
    return containsSiteAndAddress.original;
  }

  const anyAddress = normalizedColumns.find((item) =>
    item.normalized.includes('address')
  );
  if (anyAddress) {
    return anyAddress.original;
  }

  return null;
};

const TABLE_ENDPOINTS: EndpointTableConfig[] = [
  {
    name: 'Site-Cluster List',
    icon: 'table_chart',
    path: '/site-clusters',
  },
  {
    name: 'Warehouse-Cluster Distances',
    icon: 'warehouse',
    path: '/warehouse-clusters',
    requiresWarehouse: true,
  },
  {
    name: 'User-to-Closest Site Details',
    icon: 'person_pin_circle',
    path: '/user-sites',
    requiresUser: true,
  },
  {
    name: 'Site-to-Site Travel Info',
    icon: 'alt_route',
    path: '/site-to-site',
  },
];

const MAP_ENDPOINTS: MapConfig[] = [
  {
    title: 'User Map',
    description: 'User route map report.',
    icon: 'map',
    path: '/user',
    requiresUser: true,
  },
  {
    title: 'Cluster Sites Map',
    description: 'Clustered sites map report.',
    icon: 'scatter_plot',
    path: '/clustered',
  },
  {
    title: 'Warehouse Map',
    description: 'Warehouse map report.',
    icon: 'warehouse',
    path: '/warehouse',
  },
];

const sectionHeadingCss = {
  display: 'block',
  marginBottom: '0',
  paddingLeft: '12px',
  fontSize: '13px',
};

const sectionSelectorWrapCss = {
  marginBottom: '0',
};

const sectionControlBlockCss = {
  width: '100%',
  maxWidth: '420px',
};

const sectionResultWrapCss = {
  marginLeft: '12px',
  marginTop: '-4px',
};

const sectionSelectorStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  maxWidth: 'none',
  height: '32px',
  borderRadius: '0',
  border: '1px solid #c6d4e8',
  padding: '0 12px',
  paddingRight: '34px',
  fontSize: '13px',
  color: '#2d3a4a',
  backgroundColor: '#fff',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  backgroundImage:
    'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 12 12%27%3E%3Cpath fill=%27%2360758f%27 d=%27M2.2 4.2a.75.75 0 0 1 1.06 0L6 6.94 8.74 4.2a.75.75 0 1 1 1.06 1.06L6.53 8.53a.75.75 0 0 1-1.06 0L2.2 5.26a.75.75 0 0 1 0-1.06z%27/%3E%3C/svg%3E")',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
  backgroundSize: '14px 14px',
};

const normalizeBaseUrl = (baseUrl?: string) => {
  if (!baseUrl) {
    return '';
  }
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
};

const resolveRouteToolApiBase = () => {
  const configured = normalizeBaseUrl(API_URL);
  if (!configured || configured.includes('xyz-dev.com')) {
    return 'http://localhost:5000';
  }
  return configured;
};

const buildApiUrl = (baseUrl: string, path: string) => {
  if (baseUrl.endsWith('/api') && path.startsWith('/api/')) {
    return `${baseUrl}${path.slice(4)}`;
  }
  return `${baseUrl}${path}`;
};

const clusterNumberToLabel = (value: number) => {
  if (value === -1) {
    return 'Outlier';
  }

  if (value === 0) {
    return 'Group A';
  }

  return 'Group B';
};

const injectClusterHoverNormalizerScript = (html: string) => {
  return html;
};

const ensureRenderableMapHtml = (rawHtml: string) => {
  const trimmed = String(rawHtml || '').trim();
  if (!trimmed) {
    return '';
  }

  const hasHtmlMarkup = /<(html|head|body|div|script)\b/i.test(trimmed);
  const hasPlotlyCall = /Plotly\.newPlot\s*\(/.test(trimmed);

  if (hasHtmlMarkup || !hasPlotlyCall) {
    return trimmed;
  }

  const targetIdMatch = trimmed.match(
    /Plotly\.newPlot\(\s*["'`]([^"'`]+)["'`]/
  );
  const rawTargetId = targetIdMatch?.[1] || 'route-tool-plotly-map';
  const safeTargetId = rawTargetId.replace(/[^A-Za-z0-9_:\-\.]/g, '');

  return [
    '<!doctype html>',
    '<html>',
    '<head>',
    '  <meta charset="utf-8" />',
    '  <script charset="utf-8" src="https://cdn.plot.ly/plotly-3.5.0.min.js" crossorigin="anonymous"></script>',
    '</head>',
    '<body>',
    `  <div id="${safeTargetId}" style="width:100%;height:700px;"></div>`,
    `  <script>${trimmed}</script>`,
    '</body>',
    '</html>',
  ].join('\n');
};

const normalizeClusterLabelsInMapHtml = (html: string) =>
  injectClusterHoverNormalizerScript(
    html
      .replace(/(cluster\s*[:=]\s*)(-?\d+)/gi, (_match, prefix, rawNumber) => {
        const parsed = Number(rawNumber);
        if (!Number.isFinite(parsed)) {
          return _match;
        }

        return `${prefix}${clusterNumberToLabel(Math.trunc(parsed))}`;
      })
      .replace(/(cluster\s+)(-?\d+)/gi, (_match, prefix, rawNumber) => {
        const parsed = Number(rawNumber);
        if (!Number.isFinite(parsed)) {
          return _match;
        }

        return `${prefix}${clusterNumberToLabel(Math.trunc(parsed))}`;
      })
  );

const wait = (ms: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

/**
 * Reads the has_warehouse / has_user flags stored from the POST /upload response.
 * Defaults both to true when flags are absent (direct URL load, re-visit, etc.)
 * so ResultsScreen falls back to attempting all endpoints gracefully.
 */
const readUploadFlags = (
  entityName: string,
): { has_warehouse: boolean; has_user: boolean } => {
  try {
    const raw = window.sessionStorage.getItem(ROUTE_TOOL_UPLOAD_FLAGS_KEY);
    if (!raw) return { has_warehouse: true, has_user: true };
    const flags = JSON.parse(raw) as {
      entity: string;
      has_warehouse: boolean;
      has_user: boolean;
    };
    if (flags.entity !== entityName) return { has_warehouse: true, has_user: true };
    return {
      has_warehouse: flags.has_warehouse !== false,
      has_user: flags.has_user !== false,
    };
  } catch {
    return { has_warehouse: true, has_user: true };
  }
};

/**
 * Fetches per-file blob readiness from GET /api/entities/{entity}/status.
 * Returns the blobs object so the UI can filter endpoints and skip overlays
 * when output files are already present in storage.
 */
interface BlobStatus {
  site_clusters?: boolean;
  site_travel?: boolean;
  user_sites?: boolean;
  warehouse_clusters?: boolean;
  all_complete?: boolean;
}

const fetchBlobStatus = async (
  baseUrl: string,
  entityName: string,
): Promise<BlobStatus | null> => {
  if (!entityName) return null;
  try {
    const response = await fetch(
      buildApiUrl(baseUrl, `/api/entities/${encodeURIComponent(entityName)}/status`),
      { headers: getAuthHeaders() },
    );
    if (!response.ok) return null;
    const body = await response.json();
    return (body?.blobs ?? null) as BlobStatus | null;
  } catch {
    return null;
  }
};

/** True only when all output files are confirmed ready in storage. */
const isBlobsAllComplete = (blobs: BlobStatus | null): boolean =>
  blobs?.all_complete === true;

/**
 * Polls GET /api/entities/{entity}/status until the job is no longer running.
 *
 * Sentinel responses:
 *   202  { processing: true }               → still running (keep polling)
 *   200  { job: { status: "running" } }     → still running
 *   200  blobs.all_complete === false        → blobs not ready (when waitForBlobsComplete=true)
 *   200  (any other status / true blobs)    → done, proceed
 *   404                                     → not tracked, proceed immediately
 *   401/403                                 → auth issue, proceed (surface via real APIs)
 *
 * job.stage (e.g. "geocoding", "routing", "clustering") is shown to the user.
 */
const pollJobStatus = async (
  baseUrl: string,
  entityName: string,
  onStatusUpdate: (msg: string) => void,
  options: { waitForBlobsComplete?: boolean } = {},
  maxWaitMs = 120_000,
  intervalMs = 3_000,
): Promise<'ready' | 'timeout'> => {
  const url = buildApiUrl(
    baseUrl,
    `/api/entities/${encodeURIComponent(entityName)}/status`,
  );
  const startedAt = Date.now();
  const deadline = startedAt + maxWaitMs;

  while (Date.now() < deadline) {
    let response: Response;
    try {
      // eslint-disable-next-line no-await-in-loop
      response = await fetch(url, { headers: getAuthHeaders() });
    } catch {
      return 'ready'; // network error — proceed optimistically
    }

    if (response.status === 404) return 'ready'; // no status tracking for this entity
    if (response.status === 401 || response.status === 403) return 'ready'; // surface via real API calls
    if (response.status === 202) {
      // Explicit sentinel: backend says processing is still in progress
      const elapsed = Math.round((Date.now() - startedAt) / 1000);
      onStatusUpdate(`Processing in progress (${elapsed}s elapsed)… Please wait.`);
      // eslint-disable-next-line no-await-in-loop
      await wait(intervalMs);
      continue;
    }
    if (!response.ok) return 'ready'; // unexpected status — proceed

    try {
      const body = await response.json();
      const jobStatus: string = body?.job?.status || body?.status || '';
      const stage: string = body?.job?.stage || body?.stage || '';
      // For map loading, also wait until all blobs are fully written to storage.
      const blobsComplete: boolean = options.waitForBlobsComplete
        ? (body?.blobs?.all_complete ?? false)
        : true;

      const isRunning = jobStatus === 'running' || jobStatus === 'processing';
      if (isRunning || !blobsComplete) {
        const elapsed = Math.round((Date.now() - startedAt) / 1000);
        const stageLabel = stage ? ` \u2014 ${stage}` : '';
        onStatusUpdate(
          `Processing in progress${stageLabel} (${elapsed}s elapsed)\u2026 Please wait.`,
        );
        // eslint-disable-next-line no-await-in-loop
        await wait(intervalMs);
        continue;
      }
    } catch {
      // Non-JSON response body — treat as done
    }

    return 'ready';
  }

  return 'timeout';
};

const parseHtmlTable = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const table = doc.querySelector('table');

  if (!table) {
    return null;
  }

  const headerCells = Array.from(table.querySelectorAll('thead th'));
  const columns =
    headerCells.length > 0
      ? headerCells.map((th) => th.textContent?.trim() || '')
      : Array.from(
          table.querySelectorAll('tr:first-child th, tr:first-child td')
        ).map((cell) => cell.textContent?.trim() || '');

  const bodyRows = Array.from(table.querySelectorAll('tbody tr'));
  const rowElements =
    bodyRows.length > 0
      ? bodyRows
      : Array.from(table.querySelectorAll('tr')).slice(
          columns.length > 0 ? 1 : 0
        );

  const rows = rowElements
    .map((row) => {
      const cells = Array.from(row.querySelectorAll('td'));
      if (cells.length === 0) {
        return null;
      }

      const rowObj: Record<string, string> = {};
      columns.forEach((column, index) => {
        rowObj[column || `column_${index + 1}`] =
          cells[index]?.textContent?.trim() || '';
      });
      return rowObj;
    })
    .filter((item): item is Record<string, string> => Boolean(item));

  return { columns, rows };
};

const parseObjectArrayTable = (items: any[]): ParsedTable | null => {
  const objectRows = items.filter(
    (item) => item && typeof item === 'object' && !Array.isArray(item)
  ) as Record<string, any>[];

  if (objectRows.length === 0) {
    return null;
  }

  const columns: string[] = Array.from(
    objectRows.reduce<Set<string>>((set, row) => {
      Object.keys(row).forEach((key) => set.add(key));
      return set;
    }, new Set<string>())
  );

  if (columns.length === 0) {
    return null;
  }

  const rows = objectRows.map((item) => {
    const rowObj: Record<string, string | number | boolean | null | undefined> =
      {};
    columns.forEach((column) => {
      rowObj[column] = (item as Record<string, any>)[column];
    });
    return rowObj;
  });

  return { columns, rows };
};

const normalizeTablePayload = (payload: any): ParsedTable | null => {
  if (!payload) {
    return null;
  }

  if (typeof payload === 'string') {
    return parseHtmlTable(payload);
  }

  if (Array.isArray(payload)) {
    return parseObjectArrayTable(payload);
  }

  if (
    payload &&
    Array.isArray(payload.columns) &&
    Array.isArray(payload.rows)
  ) {
    return { columns: payload.columns, rows: payload.rows };
  }

  const nestedCandidates = [
    payload.table,
    payload.data,
    payload.result,
    payload.output,
    payload.payload,
  ];

  for (const candidate of nestedCandidates) {
    const parsed = normalizeTablePayload(candidate);
    if (parsed) {
      return parsed;
    }
  }

  if (typeof payload.html === 'string') {
    return parseHtmlTable(payload.html);
  }

  if (Array.isArray(payload.rows)) {
    if (Array.isArray(payload.columns)) {
      return { columns: payload.columns, rows: payload.rows };
    }
    return parseObjectArrayTable(payload.rows);
  }

  const firstArrayValue = Object.values(payload).find((value) =>
    Array.isArray(value)
  );
  if (Array.isArray(firstArrayValue)) {
    return parseObjectArrayTable(firstArrayValue);
  }

  return null;
};

const downloadCSV = (table: TableData) => {
  const csv = [
    table.columns.join(','),
    ...table.rows.map((row) =>
      table.columns.map((col) => row[col] ?? '').join(',')
    ),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${table.name.replace(/\s+/g, '_').toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const downloadTextFile = (filename: string, content: string, type: string) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const getFilenameFromContentDisposition = (
  contentDisposition: string | null
) => {
  if (!contentDisposition) {
    return null;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].trim());
  }

  const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1].trim();
  }

  const plainMatch = contentDisposition.match(/filename=([^;]+)/i);
  if (plainMatch?.[1]) {
    return plainMatch[1].trim();
  }

  return null;
};

const downloadBlobFile = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const extensionFromContentType = (contentType: string | null) => {
  const normalized = String(contentType || '').toLowerCase();
  if (normalized.includes('spreadsheetml') || normalized.includes('excel')) {
    return 'xlsx';
  }
  if (normalized.includes('csv')) {
    return 'csv';
  }
  if (normalized.includes('zip')) {
    return 'zip';
  }
  if (normalized.includes('json')) {
    return 'json';
  }
  return 'csv';
};

const downloadTableFromApi = async (
  baseUrl: string,
  entityName: string,
  path: string,
  fallbackFilename: string
) => {
  const response = await fetch(
    buildApiUrl(
      baseUrl,
      `/api/tables/${encodeURIComponent(entityName)}${path}/download`
    ),
    {
      method: 'GET',
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(`Download failed with ${response.status}`);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition');
  const apiFilename = getFilenameFromContentDisposition(disposition);
  const safeFallbackFilename = (() => {
    const extension = extensionFromContentType(
      response.headers.get('content-type')
    );
    const base = fallbackFilename.replace(/\.[a-z0-9]+$/i, '');
    return `${base}.${extension}`;
  })();
  const filename = apiFilename || safeFallbackFilename;
  downloadBlobFile(blob, filename);
};

const downloadAllTablesFromApi = async (
  baseUrl: string,
  entityName: string
) => {
  const directDownloadUrl = buildApiUrl(
    baseUrl,
    `/api/tables/${encodeURIComponent(entityName)}/download-all`
  );

  const response = await fetch(directDownloadUrl, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Download failed with ${response.status}`);
  }

  const contentType = (response.headers.get('content-type') || '').toLowerCase();

  // Backend returns an HTML trigger page with JS that normally runs in a browser tab.
  // Parse the embedded URLs and fetch each individually with auth headers instead.
  if (contentType.includes('text/html')) {
    const html = await response.text();
    const urlMatches = [...html.matchAll(/"([^"]+\/download[^"]*)"/g)];
    if (urlMatches.length === 0) {
      throw new Error('download-all returned HTML with no file URLs');
    }
    await Promise.all(
      urlMatches.map(async ([, rawUrl]) => {
        // Rebase URL to the current baseUrl in case the HTML contains localhost URLs.
        const urlPath = rawUrl.replace(/^https?:\/\/[^/]+/, '');
        const fileResponse = await fetch(buildApiUrl(baseUrl, urlPath), {
          method: 'GET',
          headers: getAuthHeaders(),
        });
        if (!fileResponse.ok) {
          return;
        }
        const blob = await fileResponse.blob();
        const disposition = fileResponse.headers.get('content-disposition');
        const fileContentType = fileResponse.headers.get('content-type') || '';
        const ext = extensionFromContentType(fileContentType);
        const segment = urlPath.split('/').filter(Boolean).slice(-2, -1)[0] || 'file';
        const filename =
          getFilenameFromContentDisposition(disposition) || `${segment}.${ext}`;
        downloadBlobFile(blob, filename);
      })
    );
    return;
  }

  // Direct file response (zip, xlsx, etc.)
  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition');
  const apiFilename = getFilenameFromContentDisposition(disposition);
  const extension = extensionFromContentType(contentType);
  const filename =
    apiFilename ||
    `${entityName.replace(/\s+/g, '_').toLowerCase()}_tables.${extension}`;
  downloadBlobFile(blob, filename);
};

const buildTableQueryParams = (
  page: number,
  pageSize: number,
  sortBy: Array<{ id?: string; desc?: boolean }> = [],
  defaultSortBy?: string,
  defaultSortDir: 'asc' | 'desc' = 'asc'
) => {
  const params = new URLSearchParams();
  const numericPage = Number(page);
  const numericPageSize = Number(pageSize);

  // DataTable passes zero-based page index (0 = first page).
  // Backend expects one-based page number (1 = first page).
  const normalizedPage =
    Number.isFinite(numericPage) && numericPage >= 0 ? numericPage + 1 : 1;
  const normalizedPageSize =
    Number.isFinite(numericPageSize) && numericPageSize > 0
      ? numericPageSize
      : 10;

  params.set('page', String(normalizedPage));
  params.set('page_size', String(normalizedPageSize));

  const primarySort =
    Array.isArray(sortBy) && sortBy.length > 0 ? sortBy[0] : null;
  if (primarySort?.id) {
    params.set('sort_by', String(primarySort.id));
    params.set('sort_dir', primarySort.desc ? 'desc' : 'asc');
  } else if (defaultSortBy) {
    params.set('sort_by', defaultSortBy);
    params.set('sort_dir', defaultSortDir);
  }

  return params.toString();
};

const findNestedTotalRows = (value: any, depth = 0): number | null => {
  if (depth > 6 || value === null || value === undefined) {
    return null;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findNestedTotalRows(item, depth + 1);
      if (found !== null) {
        return found;
      }
    }
    return null;
  }

  if (typeof value !== 'object') {
    return null;
  }

  const paginationTotal = Number((value as any)?.pagination?.total_rows);
  if (Number.isFinite(paginationTotal) && paginationTotal >= 0) {
    return paginationTotal;
  }

  const directTotalRows = Number((value as any)?.total_rows);
  if (Number.isFinite(directTotalRows) && directTotalRows >= 0) {
    return directTotalRows;
  }

  for (const nestedValue of Object.values(value)) {
    const found = findNestedTotalRows(nestedValue, depth + 1);
    if (found !== null) {
      return found;
    }
  }

  return null;
};

const parseTotalRows = (payload: any, fallback: number) => {
  const deeplyNestedTotalRows = findNestedTotalRows(payload);
  if (deeplyNestedTotalRows !== null) {
    return deeplyNestedTotalRows;
  }

  const nestedCandidates = [
    payload,
    payload?.table,
    payload?.data,
    payload?.result,
    payload?.output,
    payload?.payload,
  ].filter(Boolean);

  for (const candidate of nestedCandidates) {
    const nestedPaginationTotal = Number(candidate?.pagination?.total_rows);
    if (Number.isFinite(nestedPaginationTotal) && nestedPaginationTotal >= 0) {
      return nestedPaginationTotal;
    }

    const nestedTotalRows = Number(candidate?.total_rows);
    if (Number.isFinite(nestedTotalRows) && nestedTotalRows >= 0) {
      return nestedTotalRows;
    }
  }

  const candidates = [
    payload?.pagination?.total_rows,
    payload?.total_rows,
    payload?.count,
    payload?.total,
  ];

  for (const candidate of candidates) {
    const numeric = Number(candidate);
    if (Number.isFinite(numeric) && numeric >= 0) {
      return numeric;
    }
  }

  return fallback;
};

const DEFAULT_SORT_BY_PATH: Record<string, string> = {
  '/site-clusters': 'Cluster',
  '/warehouse-clusters': 'Average Travel Time to Cluster (hours)',
  '/user-sites': 'Travel Time (hours)',
  '/site-to-site': 'Travel Time (hours)',
};

const DEFAULT_SORT_DIR_BY_PATH: Record<string, 'asc' | 'desc'> = {
  '/site-clusters': 'asc',
  '/warehouse-clusters': 'desc',
  '/user-sites': 'desc',
  '/site-to-site': 'desc',
};

const buildInitialTableLoadQuery = (path: string) => {
  const params = new URLSearchParams();
  params.set('page', '1');
  params.set('page_size', '10');
  params.set('sort_dir', DEFAULT_SORT_DIR_BY_PATH[path] || 'asc');

  const defaultSortBy = DEFAULT_SORT_BY_PATH[path];
  if (defaultSortBy) {
    params.set('sort_by', defaultSortBy);
  }

  return params.toString();
};

const DataTableCard = ({
  table,
  siteAddressesByCluster,
  baseUrl,
  entityName,
}: {
  table: TableData;
  siteAddressesByCluster: Record<string, string[]>;
  baseUrl: string;
  entityName: string;
}) => {
  const tableStorageId = `route-tool-${entityName}-${table.path}`;
  const totalRows =
    Number.isFinite(Number(table.totalRows)) && Number(table.totalRows) >= 0
      ? Number(table.totalRows)
      : table.rows.length;
  const shouldMapClusterLabels =
    table.name === SITE_CLUSTER_TABLE_NAME ||
    table.name === WAREHOUSE_CLUSTER_TABLE_NAME;
  const shouldFormatDuration =
    table.name === WAREHOUSE_CLUSTER_TABLE_NAME ||
    table.name === 'Site-to-Site Travel Info';

  const displayColumns = useMemo(() => {
    if (table.name !== WAREHOUSE_CLUSTER_TABLE_NAME) return table.columns;
    const warehouseCol = table.columns.find((c) => {
      const n = normalizeKey(c);
      return n.includes('warehouse') && !n.includes('cluster');
    });
    const clusterCol = table.columns.find((c) => isClusterColumn(c));
    const rest = table.columns.filter((c) => c !== warehouseCol && c !== clusterCol);
    return [warehouseCol, clusterCol, ...rest].filter((c): c is string => Boolean(c));
  }, [table.columns, table.name]);

  const tableColumns = useMemo(
    () =>
      displayColumns.map((column) => ({
        Header: column,
        accessor: column,
        width: 180,
        Cell: ({ value }: any) => {
          const isClusterCell =
            shouldMapClusterLabels && isClusterColumn(column);
          const isDurationCell =
            shouldFormatDuration && isDurationColumn(column);
          const text = isClusterCell
            ? toClusterLabel(value)
            : isDurationCell
              ? toDurationLabel(value, column)
              : String(value ?? '');
          const shouldShowSiteListTooltip =
            (table.name === WAREHOUSE_CLUSTER_TABLE_NAME ||
              table.name === SITE_CLUSTER_TABLE_NAME) &&
            isClusterCell;

          const tooltipContent = (() => {
            if (isClusterCell && text === 'Outlier') {
              return <span>{OUTLIER_TOOLTIP_MESSAGE}</span>;
            }
            if (!shouldShowSiteListTooltip) {
              return null;
            }
            const matchingSiteAddresses = siteAddressesByCluster[text] || [];
            return (
              <div style={{ maxWidth: 300, padding: '4px 0' }}>
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: 6,
                    paddingBottom: 4,
                    borderBottom: '1px solid #d0d7e3',
                    fontSize: 13,
                  }}
                >
                  {text}{' '}—{' '}
                  {matchingSiteAddresses.length} site
                  {matchingSiteAddresses.length !== 1 ? 's' : ''}
                </div>
                {matchingSiteAddresses.length === 0 ? (
                  <div style={{ color: '#888', fontSize: 12 }}>
                    No matching site addresses found.
                  </div>
                ) : (
                  <ul style={{ margin: 0, padding: '0 0 0 14px' }}>
                    {matchingSiteAddresses.map((addr: string, i: number) => (
                      <li key={i} style={{ marginBottom: 2, fontSize: 12 }}>
                        {addr}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })();

          const inner = (
            <div
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                textDecoration: shouldShowSiteListTooltip
                  ? 'underline dotted'
                  : 'none',
                cursor: shouldShowSiteListTooltip ? 'pointer' : 'default',
              }}
            >
              {text}
            </div>
          );

          if (!tooltipContent) return inner;

          return (
            <V2Tooltip content={tooltipContent} position="top" variant="white">
              {inner}
            </V2Tooltip>
          );
        },
      })),
    [
      siteAddressesByCluster,
      shouldMapClusterLabels,
      shouldFormatDuration,
      displayColumns,
      table.name,
    ]
  );

  const apiPaginationCall = async (
    page: number,
    pageSize: number,
    sortBy: Array<{ id?: string; desc?: boolean }> = []
  ) => {
    const query = buildTableQueryParams(
      page,
      pageSize,
      sortBy,
      DEFAULT_SORT_BY_PATH[table.path],
      DEFAULT_SORT_DIR_BY_PATH[table.path] || 'asc'
    );
    const response = await fetch(
      buildApiUrl(
        baseUrl,
        `/api/tables/${encodeURIComponent(entityName)}${table.path}?${query}`
      ),
      { headers: getAuthHeaders() }
    );

    if (!response.ok) {
      return {
        results: [],
        rows: [],
        data: [],
        count: 0,
        totalCount: 0,
      };
    }

    const contentType = response.headers.get('content-type') || '';
    let payload: any;
    if (contentType.includes('application/json')) {
      payload = await response.json();
    } else {
      const textPayload = await response.text();
      try {
        payload = JSON.parse(textPayload);
      } catch {
        payload = textPayload;
      }
    }

    const parsed = normalizeTablePayload(payload);
    const rows = parsed?.rows || [];
    const totalCount = parseTotalRows(payload, rows.length);
    const totalPages =
      Number(payload?.pagination?.total_pages || 0) || undefined;
    const pagination = payload?.pagination || undefined;

    return {
      results: rows,
      rows,
      data: rows,
      count: totalCount,
      totalCount,
      totalRecords: totalCount,
      totalRows: totalCount,
      totalPages,
      pageCount: totalPages,
      pagination,
    };
  };

  // When filters are active we override the pagination total with the filtered count.
  // null means "use the server total" (unfiltered state).
  const [overrideTotalRows, setOverrideTotalRows] = useState<number | null>(null);

  const tableState = useDataTable({
    initialData: table.rows as any[],
    initialColumns: tableColumns as any[],
    uniqueStorageId: tableStorageId,
    apiPaginationCall,
    apiQueryOptions: { disablePrefetching: true, requestPolicy: 'no-cache' },
    paginationResultsTotalCount: overrideTotalRows ?? totalRows,
    manualSortBy: true,
    showPagination: true,
    showGlobalFilter: false,
    showFilterDataset: true,
    showTableSettings: false,
    showTopPagination: true,
    showBottomPagination: true,
    paginationBottomCompact: false,
    pageSizeOptions: [10, 25, 50, 100],
    pageSizeDefault: 10,
    customHeaderSection: (
      <button
        type="button"
        onClick={() => {
          const btn = document.querySelector(
            '.abyss-data-table-filter-dataset-button'
          ) as HTMLElement;
          if (btn) {
            btn.click();
          }
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: '#196ECF',
          fontSize: 'var(--abyss-fontSizes-pagination-fontSize-resultsLabel)',
          fontWeight: 600,
          padding: 'var(--abyss-space-pagination-space-padding-resultsLabel)',
          lineHeight: 'var(--abyss-lineHeights-pagination-lineHeight-resultsLabel)',
          alignSelf: 'center',
        }}
      >
        <IconSymbol icon="filter_list" size={16} color="#196ECF" variant="outlined" />
        Filter Dataset
      </button>
    ),
  });

  useEffect(() => {
    tableState.setData(table.rows as any[]);
  }, [table.rows]);

  useEffect(() => {
    const resetToFirstPage = async () => {
      try {
        await tableState.pagination.setPageSize(
          tableState.state.pageSize || 10
        );
        await tableState.pagination.gotoPage(0);
      } catch {
        // no-op: table is still usable even if pagination reset is unavailable
      }
    };

    resetToFirstPage();
  }, [table.name]);

  // When the built-in "Filter Dataset" is active, fetch ALL rows so the DataTable
  // can manage pagination client-side over the correct filtered total.
  // Without this, pagination stays at the unfiltered server total (wrong page count).
  const lastFilterKeyRef = React.useRef('');

  useEffect(() => {
    const filters: Array<{ id: string; value: any }> = (tableState.state as any)?.filters ?? [];
    const filterKey = JSON.stringify(filters);

    if (filterKey === lastFilterKeyRef.current) return; // no change, skip
    lastFilterKeyRef.current = filterKey;

    if (filters.length === 0) {
      // Filters cleared — restore original page data and server total
      tableState.setData(table.rows as any[]);
      setOverrideTotalRows(null);
      return;
    }

    // Filters active — pull all rows once so client-side filter + pagination are accurate
    void (async () => {
      try {
        const params = new URLSearchParams();
        params.set('page', '1');
        params.set('page_size', '10000');
        const resp = await fetch(
          buildApiUrl(
            baseUrl,
            `/api/tables/${encodeURIComponent(entityName)}${table.path}?${params.toString()}`
          ),
          { headers: getAuthHeaders() }
        );
        if (resp.ok) {
          const payload = await resp.json().catch(() => ({}));
          const parsed = normalizeTablePayload(payload);
          if (parsed?.rows) {
            tableState.setData(parsed.rows as any[]);
            // Override pagination total so page count reflects the filtered dataset
            setOverrideTotalRows(parsed.rows.length);
          }
        }
      } catch {
        // keep current data on network failure
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(tableState.state as any)?.filters]);

  return (
    <Card css={{ marginTop: '0', marginBottom: '16px' }}>
      <Box
        css={{
          padding: '8px 12px',
          backgroundColor: '#f5f7fa',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Layout.Group
          alignItems="center"
          space={8}
          css={{ justifyContent: 'space-between', width: '100%' }}
        >
          <Layout.Group alignItems="center" space={8}>
            <IconSymbol icon={table.icon as any} size={18} color="$primary1" />
            <Label fontWeight="bold" size="sm" color="$gray8">
              {table.name}
            </Label>
            <Label size="xs" color="$gray5">
              ({totalRows} rows)
            </Label>
          </Layout.Group>
          <Layout.Group alignItems="center" space={8}>
            <Button
              size="$sm"
              variant="outline"
              onClick={async () => {
                try {
                  await downloadTableFromApi(
                    baseUrl,
                    entityName,
                    table.path,
                    `${table.name.replace(/\s+/g, '_').toLowerCase()}.csv`
                  );
                } catch {
                  // Keep UI resilient by falling back to in-memory CSV generation.
                  downloadCSV(table);
                }
              }}
            >
              <IconSymbol icon="download" size={14} />
              Download CSV
            </Button>
          </Layout.Group>
        </Layout.Group>
      </Box>
      <Box
        css={{
          '& > div > .abyss-c-dgDaSO:has(~ .abyss-c-dgDaSO)': {
            position: 'absolute !important',
            width: '1px !important',
            height: '1px !important',
            overflow: 'hidden !important',
            clip: 'rect(0, 0, 0, 0) !important',
            whiteSpace: 'nowrap !important',
            border: '0 !important',
            padding: '0 !important',
            margin: '-1px !important',
          },
          '& > div > .abyss-c-dgDaSO + *:not(.abyss-c-dgDaSO):has(+ .abyss-c-dgDaSO)': {
            display: 'none !important',
          },
          '& > div > .abyss-c-dgDaSO': {
            padding: '4px 8px !important',
            flexWrap: 'nowrap !important',
            alignItems: 'flex-start !important',
            justifyContent: 'flex-end !important',
            gap: '8px !important',
          },
          '& > div > .abyss-c-dgDaSO > div:last-child': {
            marginLeft: '0 !important',
            display: 'flex !important',
            alignItems: 'flex-start !important',
            gap: '8px !important',
          },
          '& > div > .abyss-c-dgDaSO > div:first-child': {
            display: 'flex !important',
            alignItems: 'flex-start !important',
            paddingTop: '12px !important',
          },
          '&& table th, && table td': {
            paddingTop: '6px',
            paddingBottom: '6px',
            paddingLeft: '8px',
            paddingRight: '8px',
            fontSize: '12px',
            lineHeight: '1.2',
          },
          overflowX: 'auto',
        }}
      >
        {table.columns.length === 0 && table.rows.length === 0 ? (
          <Box css={{ padding: '12px 16px' }}>
            <Label size="sm" color="$gray6">
              No table content available for this section.
            </Label>
          </Box>
        ) : (
          <DataTable title="" tableState={tableState} hideTitleHeader />
        )}
      </Box>
    </Card>
  );
};

const MapPlaceholder = ({
  title,
  description,
  icon,
  html,
  error,
}: {
  title: string;
  description: string;
  icon: string;
  html: string;
  error?: string;
}) => {
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  return (
    <>
      {isFullscreen && html && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 9999,
            backgroundColor: '#000',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 16px',
              backgroundColor: '#1a2d4a',
              color: '#fff',
              flexShrink: 0,
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 14 }}>{title}</span>
            <button
              onClick={() => setIsFullscreen(false)}
              style={{
                background: 'none',
                border: '1px solid rgba(255,255,255,0.4)',
                color: '#fff',
                cursor: 'pointer',
                padding: '4px 14px',
                borderRadius: 4,
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              ✕ Exit Full Screen
            </button>
          </div>
          <iframe
            title={`${title} (fullscreen)`}
            srcDoc={html}
            style={{ width: '100%', flex: 1, border: 0 }}
          />
        </div>
      )}
      <Card css={{ marginTop: '0', marginBottom: '16px' }}>
        <Box
          css={{
            padding: '8px 12px',
            backgroundColor: '#f5f7fa',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Layout.Group
            alignItems="center"
            space={8}
            css={{ justifyContent: 'space-between', width: '100%' }}
          >
            <Layout.Group alignItems="center" space={8}>
              <IconSymbol icon="map" size={18} color="$primary1" />
              <Label fontWeight="bold" size="sm" color="$gray8">
                {title}
              </Label>
            </Layout.Group>
            {html && (
              <Button
                size="$sm"
                variant="outline"
                onClick={() => setIsFullscreen(true)}
              >
                <IconSymbol icon="fullscreen" size={14} />
                Full Screen
              </Button>
            )}
          </Layout.Group>
        </Box>
        <Box
          css={{
            height: 'clamp(280px, 45vh, 380px)',
          }}
        >
          {html ? (
            <iframe
              title={title}
              srcDoc={html}
              style={{ width: '100%', height: '100%', border: 0 }}
            />
          ) : (
            <Box css={{ padding: '20px 16px' }}>
              <Label size="sm" color="$gray6">
                {error || 'Map data is not available for this view yet.'}
              </Label>
            </Box>
          )}
        </Box>
      </Card>
    </>
  );
};

export const ResultsScreen = () => {
  const { navigate, getRouteParams, getLocation } = useRouter();
  const { id } = getRouteParams();
  const location = getLocation();
  const { UpdatePageTitle } = PageTitleService();
  const { UpdateBreadcrumb } = BreadcrumbService();
  const initialTab: TabKey = (location?.pathname || '').includes('/route-tool/maps/') ? 'maps' : 'tables';
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [tables, setTables] = useState<TableData[]>([]);
  const [selectedTableName, setSelectedTableName] = useState('');
  // Start tablesLoading=true on the tables route so the overlay shows from the very first render
  // before any async work begins, preventing blank flicker (mirrors mapsLoading pattern).
  const [tablesLoading, setTablesLoading] = useState(initialTab === 'tables');
  const [tablesError, setTablesError] = useState('');
  // Start mapsLoading=true on the maps route so the overlay shows from the very first render
  // before any async work begins, preventing blank flicker.
  const [mapsLoading, setMapsLoading] = useState(initialTab === 'maps');
  const [mapsError, setMapsError] = useState('');
  const [mapsHtml, setMapsHtml] = useState<Record<string, string>>({});
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [transitioningToMaps, setTransitioningToMaps] = useState(false);
  // True once all blobs are confirmed ready — gates the Next button.
  const [mapBlobsReady, setMapBlobsReady] = useState(false);

  const isLoadingData = tablesLoading || mapsLoading;
  // Overlay on step 3: while tables loading OR transitioning to step 4 (maps not ready).
  // Overlay on step 4: PowerBIMapViewer manages its own loading skeleton, so only
  // the transitioningToMaps flag (brief navigation guard) blocks the maps tab UI.
  const isOverlayActive =
    activeTab === 'maps'
      ? transitioningToMaps
      : tablesLoading || transitioningToMaps;

  useEffect(() => {
    if (!isOverlayActive) {
      if (loadingProgress > 0) {
        setLoadingProgress(100);
        const timer = setTimeout(() => setLoadingProgress(0), 600);
        return () => clearTimeout(timer);
      }
      return undefined;
    }

    setLoadingProgress(5);
    const interval = setInterval(() => {
      setLoadingProgress((prev) => {
        const increment = Math.random() * 8 + 3;
        const next = prev + increment;
        return next >= 90 ? 90 : next;
      });
    }, 700);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOverlayActive]);

  const baseUrl = resolveRouteToolApiBase();
  const entityName = decodeURIComponent(String(id || '')).trim();
  const uploadStepUrl = `/route-tool/upload?step=2&fromResults=1&entity=${encodeURIComponent(entityName || String(id || ''))}`;
  const isStep3Ready = !tablesLoading && tables.length > 0;
  const currentStepperStep: 2 | 3 | 4 =
    activeTab === 'maps' ? 4 : isStep3Ready ? 3 : 2;

  useEffect(() => {
    (async () => {
      await UpdatePageTitle(Title);
      await UpdateBreadcrumb(Breadcrumbs);
    })();
  }, []);

  useEffect(() => {
    const path = location?.pathname || '';

    if (path.includes('/route-tool/maps/')) {
      setActiveTab('maps');
      return;
    }

    setActiveTab('tables');
  }, [location?.pathname]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location?.pathname]);

  useEffect(() => {
    if (activeTab !== 'tables' || tablesLoading || tables.length === 0) {
      return;
    }

    document.getElementById('route-tool-tables-section')?.scrollIntoView({
      block: 'start',
      behavior: 'auto',
    });
  }, [activeTab, tablesLoading, tables.length]);

  useEffect(() => {
    if (tables.length === 0) {
      setSelectedTableName('');
      return;
    }

    const exists = tables.some((table) => table.name === selectedTableName);
    if (!exists) {
      setSelectedTableName(tables[0].name);
    }
  // Intentionally only re-run when tables changes, not on every selectedTableName change.
  // selectedTableName is read here only to check existence, not as a trigger.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables]);

  const refreshSelectedTable = async (tableName: string) => {
    const selectedConfig = TABLE_ENDPOINTS.find(
      (item) => item.name === tableName
    );
    if (!selectedConfig || !entityName) {
      return;
    }

    try {
      const query = buildInitialTableLoadQuery(selectedConfig.path);
      const response = await fetch(
        buildApiUrl(
          baseUrl,
          `/api/tables/${encodeURIComponent(entityName)}${selectedConfig.path}?${query}`
        ),
        {
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        return;
      }

      const contentType = response.headers.get('content-type') || '';
      let payload: any;

      if (contentType.includes('application/json')) {
        payload = await response.json();
      } else {
        const textPayload = await response.text();
        try {
          payload = JSON.parse(textPayload);
        } catch {
          payload = textPayload;
        }
      }

      const parsed = normalizeTablePayload(payload);
      if (
        !parsed ||
        !Array.isArray(parsed.columns) ||
        !Array.isArray(parsed.rows)
      ) {
        return;
      }

      const normalizedRows = parsed.rows.filter(
        (row) => row && typeof row === 'object' && !Array.isArray(row)
      ) as Record<string, string | number | boolean | null | undefined>[];

      const normalizedColumns = parsed.columns
        .map((column) => String(column || '').trim())
        .filter(Boolean);

      setTables((previous) =>
        previous.map((table) =>
          table.name === tableName
            ? {
                ...table,
                columns: normalizedColumns,
                rows: normalizedRows,
                totalRows: parseTotalRows(payload, normalizedRows.length),
              }
            : table
        )
      );
    } catch {
      // no-op: table keeps last known data
    }
  };

  useEffect(() => {
    if (!selectedTableName) {
      return;
    }

    refreshSelectedTable(selectedTableName);
  }, [selectedTableName]);

  const loadTables = async (blobsReady = false, blobStatus: BlobStatus | null = null) => {
    if (!entityName) {
      setTables([]);
      setTablesError('No acquired entity provided. Please upload files first.');
      return;
    }

    if (!blobsReady) {
      setTablesLoading(true);
    }
    setTablesError('');

    try {
      if (!blobsReady) {
        // Wait until the backend job is no longer running before fetching tables.
        const pollResult = await pollJobStatus(
          baseUrl,
          entityName,
          (msg) => setTablesError(msg),
        );
        if (pollResult === 'timeout') {
          setTables([]);
          setTablesError(
            'Processing is taking longer than expected. Click Refresh Data to try again.',
          );
          return;
        }
        setTablesError('');
      }

      // Only fetch endpoints for file types the user actually uploaded.
      // Defaults to all endpoints when flags are absent (direct URL / re-visit).
      const uploadFlags = readUploadFlags(entityName);
      const activeEndpoints = TABLE_ENDPOINTS.filter((ep) => {
        if (ep.requiresWarehouse && !uploadFlags.has_warehouse) return false;
        if (ep.requiresUser && !uploadFlags.has_user) return false;
        // Blob-level filtering: skip endpoints whose output file isn't ready yet
        if (blobStatus !== null) {
          if (ep.path === '/site-clusters' && blobStatus.site_clusters === false) return false;
          if (ep.path === '/site-to-site' && blobStatus.site_travel === false) return false;
          if (ep.path === '/user-sites' && blobStatus.user_sites === false) return false;
          if (ep.path === '/warehouse-clusters' && blobStatus.warehouse_clusters === false) return false;
        }
        return true;
      });
      const requiredEndpointCount = activeEndpoints.length;

      const maxAttempts = 5;

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        let sawProcessing404 = false;

        const loaded = await Promise.all(
          activeEndpoints.map(async (endpoint) => {
            const initialQuery = buildInitialTableLoadQuery(endpoint.path);
            const response = await fetch(
              buildApiUrl(
                baseUrl,
                `/api/tables/${encodeURIComponent(entityName)}${endpoint.path}?${initialQuery}`
              ),
              {
                headers: getAuthHeaders(),
              }
            );

            if (response.status === 404) {
              sawProcessing404 = true;
              return null;
            }

            if (!response.ok) {
              throw new Error(
                `${endpoint.name} failed with ${response.status}`
              );
            }

            const contentType = response.headers.get('content-type') || '';
            let payload: any;

            if (contentType.includes('application/json')) {
              payload = await response.json();
            } else {
              const textPayload = await response.text();
              try {
                payload = JSON.parse(textPayload);
              } catch {
                payload = textPayload;
              }
            }

            const parsed = normalizeTablePayload(payload);

            if (
              !parsed ||
              !Array.isArray(parsed.columns) ||
              !Array.isArray(parsed.rows)
            ) {
              return null;
            }

            const normalizedRows = parsed.rows.filter(
              (row) => row && typeof row === 'object' && !Array.isArray(row)
            ) as Record<string, string | number | boolean | null | undefined>[];

            const normalizedColumns = parsed.columns
              .map((column) => String(column || '').trim())
              .filter(Boolean);

            if (normalizedColumns.length === 0 && normalizedRows.length > 0) {
              Object.keys(normalizedRows[0] || {}).forEach((key) => {
                if (key && !normalizedColumns.includes(key)) {
                  normalizedColumns.push(key);
                }
              });
            }

            if (normalizedColumns.length === 0 && normalizedRows.length === 0) {
              return null;
            }

            return {
              name: endpoint.name,
              icon: endpoint.icon,
              path: endpoint.path,
              columns: normalizedColumns,
              rows: normalizedRows,
              totalRows: parseTotalRows(payload, normalizedRows.length),
            } as TableData;
          })
        );

        const validTables = loaded.filter((table): table is TableData =>
          Boolean(table)
        );

        // Accept partial results only if we got all expected tables OR on the last attempt.
        // If some tables came back empty/null and it's not the last attempt, keep retrying.
        const allTablesLoaded = validTables.length === requiredEndpointCount;
        const isLastAttempt = attempt === maxAttempts;

        if (validTables.length > 0 && (allTablesLoaded || isLastAttempt)) {
          setTables(validTables);
          setTablesError('');
          return;
        }

        if (validTables.length > 0 && !allTablesLoaded) {
          // Partial data — update what we have but keep retrying for the rest
          setTables(validTables);
          sawProcessing404 = true; // force retry
        }

        if (!sawProcessing404) {
          if (validTables.length > 0) {
            setTablesError('');
            return;
          }
          break;
        }

        if (attempt < maxAttempts) {
          setTablesError(
            `Processing is still running. Retrying table fetch (${attempt}/${maxAttempts - 1})...`
          );
          await wait(3000);
        }
      }

      setTables([]);
      setTablesError(
        'Output files are not ready yet. Processing may still be in progress. Click Refresh Data in a few moments.'
      );
    } catch (error: any) {
      setTables([]);
      setTablesError(
        error?.message ||
          'Unable to load dynamic table data. Please verify backend/API access.'
      );
    } finally {
      setTablesLoading(false);
    }
  };

  const loadMaps = async (blobsReady = false, blobStatus: BlobStatus | null = null): Promise<Record<string, string>> => {
    if (!entityName) {
      setMapsHtml({});
      setMapsError('No acquired entity provided. Please upload files first.');
      return {};
    }

    if (!blobsReady) {
      setMapsLoading(true);
    }
    setMapsError('');

    try {
      if (!blobsReady) {
        // Wait until blobs are fully written before trying to read map files.
        // job.stage is forwarded to the user as a progress indicator.
        const pollResult = await pollJobStatus(
          baseUrl,
          entityName,
          (msg) => setMapsError(msg),
          { waitForBlobsComplete: true },
        );
        if (pollResult === 'timeout') {
          setMapsHtml({});
          setMapsError(
            'Processing is taking longer than expected. Click Refresh Data to try again.',
          );
          return {};
        }
        setMapsError('');
      }

      // Only fetch map endpoints for file types the user actually uploaded.
      const uploadFlags = readUploadFlags(entityName);
      const activeMapEndpoints = MAP_ENDPOINTS.filter((ep) => {
        if (ep.requiresUser && !uploadFlags.has_user) return false;
        return true;
      });

      const loaded = await Promise.all(
        activeMapEndpoints.map(async (endpoint) => {
          const response = await fetch(
            buildApiUrl(
              baseUrl,
              `/api/maps/${encodeURIComponent(entityName)}${endpoint.path}`
            ),
            {
              headers: getAuthHeaders(),
            }
          );

          if (response.status === 404) {
            return { path: endpoint.path, html: '' };
          }

          if (!response.ok) {
            throw new Error(`${endpoint.title} failed with ${response.status}`);
          }

          const contentType = (
            response.headers.get('content-type') || ''
          ).toLowerCase();
          const responseText = await response.text();
          let rawHtml = '';

          const parseJsonText = (text: string): any | null => {
            try {
              return JSON.parse(text);
            } catch {
              return null;
            }
          };

          const parsed = parseJsonText(responseText);

          if (parsed && typeof parsed === 'object') {
            rawHtml = String(
              parsed?.html || parsed?.data?.html || parsed?.map_html || ''
            );
          } else if (typeof parsed === 'string') {
            rawHtml = parsed;
          } else {
            // Fallback: use payload as raw HTML/script even when content-type is JSON.
            rawHtml = responseText;
          }

          if (!rawHtml && contentType.includes('application/json')) {
            rawHtml = responseText;
          }

          const renderableHtml = ensureRenderableMapHtml(rawHtml);
          const normalizedHtml =
            endpoint.path === '/clustered'
              ? normalizeClusterLabelsInMapHtml(renderableHtml)
              : renderableHtml;
          return { path: endpoint.path, html: normalizedHtml };
        })
      );

      const htmlByPath: Record<string, string> = {};
      loaded.forEach((item) => {
        if (item.html) {
          htmlByPath[item.path] = item.html;
        }
      });
      setMapsHtml(htmlByPath);
      setMapsError('');

      // Cache for instant hydration when navigating to step 4
      if (Object.keys(htmlByPath).length > 0) {
        try {
          window.sessionStorage.setItem(
            ROUTE_TOOL_MAPS_CACHE_KEY,
            JSON.stringify({ entity: entityName, htmlByPath })
          );
        } catch {
          // non-critical
        }
      }

      return htmlByPath;
    } catch (error: any) {
      setMapsHtml({});
      setMapsError(
        error?.message ||
          'Unable to load map outputs. Please verify backend/API access.'
      );
      return {};
    } finally {
      setMapsLoading(false);
    }
  };

  const ROUTE_TOOL_PRELOAD_KEY = 'routeToolPreload';

  const tryHydrateFromPreload = (): boolean => {
    try {
      const raw = window.sessionStorage.getItem(ROUTE_TOOL_PRELOAD_KEY);
      if (!raw) return false;

      const preload = JSON.parse(raw) as {
        entity: string;
        tablePayloads: Record<string, any>;
        mapPayloads: Record<string, string>;
      };

      // Only use if entity matches
      if (!preload.entity || preload.entity !== entityName) return false;

      window.sessionStorage.removeItem(ROUTE_TOOL_PRELOAD_KEY);

      // Hydrate tables
      const loadedTables = TABLE_ENDPOINTS.map((endpoint) => {
        const payload = preload.tablePayloads?.[endpoint.path];
        if (!payload) return null;
        const parsed = normalizeTablePayload(
          typeof payload === 'string' ? (() => { try { return JSON.parse(payload); } catch { return payload; } })() : payload
        );
        if (!parsed || !Array.isArray(parsed.columns) || !Array.isArray(parsed.rows)) return null;
        const normalizedColumns = parsed.columns.map((c) => String(c || '').trim()).filter(Boolean);
        const normalizedRows = parsed.rows.filter(
          (row) => row && typeof row === 'object' && !Array.isArray(row)
        ) as Record<string, string | number | boolean | null | undefined>[];
        if (normalizedColumns.length === 0 && normalizedRows.length === 0) return null;
        return {
          name: endpoint.name,
          icon: endpoint.icon,
          path: endpoint.path,
          columns: normalizedColumns,
          rows: normalizedRows,
          totalRows: parseTotalRows(payload, normalizedRows.length),
        } as TableData;
      }).filter((t): t is TableData => Boolean(t));

      if (loadedTables.length > 0) {
        setTables(loadedTables);
      }

      // Hydrate maps
      const htmlByPath: Record<string, string> = {};
      MAP_ENDPOINTS.forEach((endpoint) => {
        const rawText = preload.mapPayloads?.[endpoint.path] || '';
        if (!rawText) return;
        let rawHtml = '';
        try {
          const parsed = JSON.parse(rawText);
          if (parsed && typeof parsed === 'object') {
            rawHtml = String(parsed?.html || parsed?.data?.html || parsed?.map_html || '');
          } else if (typeof parsed === 'string') {
            rawHtml = parsed;
          } else {
            rawHtml = rawText;
          }
        } catch {
          rawHtml = rawText;
        }
        const renderableHtml = ensureRenderableMapHtml(rawHtml);
        if (!renderableHtml) return; // skip endpoints with no renderable content
        htmlByPath[endpoint.path] =
          endpoint.path === '/clustered'
            ? normalizeClusterLabelsInMapHtml(renderableHtml)
            : renderableHtml;
      });

      if (Object.keys(htmlByPath).length > 0) {
        setMapsHtml(htmlByPath);
        // Cache maps so step 4 mount can hydrate instantly without re-fetching
        try {
          window.sessionStorage.setItem(
            ROUTE_TOOL_MAPS_CACHE_KEY,
            JSON.stringify({ entity: entityName, htmlByPath })
          );
        } catch {
          // sessionStorage full or unavailable — non-critical
        }
      }

      return loadedTables.length > 0;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    let cancelled = false;

    const hydrated = tryHydrateFromPreload();
    if (hydrated) {
      // Preload hydrated tables (and maps if available).
      // Always resolve both loading flags so overlays never get stuck when preload
      // fires directly on either route (e.g. direct URL or browser back).
      setTablesLoading(false);
      setMapsLoading(false);
      setMapBlobsReady(true); // data was preloaded only after blobs were ready
      return () => { cancelled = true; };
    }

    if (initialTab === 'maps') {
      // Step 4 mount: try to use cached maps from step 3 before hitting the API
      try {
        const raw = window.sessionStorage.getItem(ROUTE_TOOL_MAPS_CACHE_KEY);
        if (raw) {
          const cached = JSON.parse(raw) as { entity: string; htmlByPath: Record<string, string> };
          const hasNonEmptyMaps = Object.values(cached.htmlByPath).some((v) => Boolean(String(v || '').trim()));
          if (cached.entity === entityName && hasNonEmptyMaps) {
            window.sessionStorage.removeItem(ROUTE_TOOL_MAPS_CACHE_KEY);
            setMapsHtml(cached.htmlByPath);
            setMapsLoading(false);
            setMapBlobsReady(true);
            loadTables();
            return () => { cancelled = true; };
          }
        }
      } catch {
        // fall through to normal load
      }
    }

    void (async () => {
      const blobs = await fetchBlobStatus(baseUrl, entityName);
      if (cancelled) return;

      const blobsAllComplete = isBlobsAllComplete(blobs);

      if (blobsAllComplete) {
        setTablesLoading(false);
        setMapsLoading(false);
      }

      loadTables(blobsAllComplete, blobs);

      if (blobsAllComplete) {
        // All blobs are ready — enable Next button and start loading maps.
        setMapBlobsReady(true);
        loadMaps(true, blobs);
      } else {
        // Blobs not ready yet — poll every 3s until all_complete is true,
        // then enable the Next button and kick off the map fetch.
        const deadline = Date.now() + 300_000; // 5-minute max wait
        let currentBlobs = blobs;
        while (Date.now() < deadline && !cancelled) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise<void>((resolve) => { setTimeout(resolve, 3_000); });
          if (cancelled) return;
          // eslint-disable-next-line no-await-in-loop
          const fresh = await fetchBlobStatus(baseUrl, entityName);
          if (cancelled) return;
          currentBlobs = fresh;
          if (isBlobsAllComplete(fresh)) {
            setMapBlobsReady(true);
            loadMaps(true, fresh);
            return;
          }
        }
        if (!cancelled) {
          // Timeout — enable anyway so the user isn’t stuck
          setMapBlobsReady(true);
          loadMaps(false, currentBlobs);
        }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const tabStyle = (tab: TabKey): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    border: 'none',
    borderBottom: `3px solid ${activeTab === tab ? '#196ECF' : 'transparent'}`,
    backgroundColor: 'transparent',
    color: activeTab === tab ? '#196ECF' : '#555',
    cursor: 'pointer',
    fontWeight: activeTab === tab ? 600 : 400,
    fontSize: '14px',
    transition: 'all 0.2s',
  });

  const selectedTable =
    tables.find((table) => table.name === selectedTableName) ||
    tables[0] ||
    null;
  const siteAddressesByCluster = useMemo(() => {
    const siteTable = tables.find(
      (table) => table.name === SITE_CLUSTER_TABLE_NAME
    );
    if (!siteTable) {
      return {} as Record<string, string[]>;
    }

    const clusterColumn = siteTable.columns.find((column) =>
      isClusterColumn(column)
    );
    if (!clusterColumn) {
      return {} as Record<string, string[]>;
    }

    const addressColumn = findPreferredAddressColumn(siteTable.columns);
    if (!addressColumn) {
      return {} as Record<string, string[]>;
    }

    const grouped = siteTable.rows.reduce<Record<string, Set<string>>>(
      (acc, row) => {
        const clusterLabel = toClusterLabel(row[clusterColumn]);
        const address = String(row[addressColumn] ?? '').trim();

        if (!clusterLabel || !address) {
          return acc;
        }

        if (!acc[clusterLabel]) {
          acc[clusterLabel] = new Set<string>();
        }

        acc[clusterLabel].add(address);
        return acc;
      },
      {}
    );

    return Object.fromEntries(
      Object.entries(grouped).map(([key, values]) => [key, Array.from(values)])
    );
  }, [tables]);

  const downloadAllFiles = async () => {
    if (activeTab === 'maps') {
      MAP_ENDPOINTS.forEach((map) => {
        const html = mapsHtml[map.path] || '';
        if (!String(html).trim()) {
          return;
        }

        downloadTextFile(
          `${map.title.replace(/\s+/g, '_').toLowerCase()}.html`,
          html,
          'text/html'
        );
      });
      return;
    }

    try {
      await downloadAllTablesFromApi(baseUrl, entityName);
      return;
    } catch {
      await Promise.all(
        TABLE_ENDPOINTS.map(async (table) => {
          try {
            await downloadTableFromApi(
              baseUrl,
              entityName,
              table.path,
              `${table.name.replace(/\s+/g, '_').toLowerCase()}.csv`
            );
          } catch {
            const inMemoryTable = tables.find(
              (item) => item.path === table.path
            );
            if (inMemoryTable) {
              downloadCSV(inMemoryTable);
            }
          }
        })
      );
    }
  };

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        gap: '12px',
        height: 'auto',
        minHeight: 'auto',
        overflow: 'visible',
      }}
    >
      <RouteToolStepper currentStep={currentStepperStep} />

      {isOverlayActive && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 10000,
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.24)',
              padding: '36px 40px',
              width: '100%',
              maxWidth: '440px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px',
            }}
          >
            <div
              style={{
                width: '52px',
                height: '52px',
                borderRadius: '50%',
                border: '4px solid #d8dde6',
                borderTopColor: '#196ecf',
                animation: 'routeToolSpin 0.9s linear infinite',
              }}
            />
            <div
              style={{
                fontSize: '15px',
                fontWeight: 700,
                color: '#1a2d4a',
                textAlign: 'center',
              }}
            >
              {transitioningToMaps
                ? 'Loading Map Data'
                : tablesLoading
                  ? 'Loading Data Tables'
                  : 'Loading Map Data'}
            </div>
            <div
              style={{
                fontSize: '13px',
                color: '#6b7a8d',
                textAlign: 'center',
                lineHeight: '1.5',
              }}
            >
              {transitioningToMaps
                ? 'Preparing maps, please wait…'
                : 'Fetching results from the server, please wait…'}
            </div>

            <div style={{ width: '100%' }}>
              <div
                style={{
                  width: '100%',
                  height: '10px',
                  backgroundColor: '#e8edf4',
                  borderRadius: '6px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${loadingProgress}%`,
                    backgroundColor: '#196ecf',
                    borderRadius: '6px',
                    transition: 'width 0.5s ease',
                  }}
                />
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: '#6b7a8d',
                  textAlign: 'right',
                  marginTop: '6px',
                  fontWeight: 600,
                }}
              >
                {Math.round(loadingProgress)}%
              </div>
            </div>
          </div>
          <style>{`
            @keyframes routeToolSpin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      <Card
        css={{
          background: '#ffffff',
          border: '1px solid #d9e0eb',
          boxShadow: '0 2px 8px rgba(20, 40, 80, 0.06)',
        }}
      >
      <Card.Section css={{ backgroundColor: '#ffffff', padding: '8px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'nowrap', minWidth: 0 }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#002677', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>
              Acquired Entity: {entityName || id}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              <Button
                size="$sm"
                variant="outline"
                disabled={tables.length === 0 && Object.keys(mapsHtml).length === 0}
                onClick={() => { void downloadAllFiles(); }}
              >
                <IconSymbol icon="download" size={13} />
                Download All Files
              </Button>
              <Button
                size="$sm"
                onClick={() => {
                  if (activeTab === 'maps') {
                    if (Object.keys(mapsHtml).length > 0) { return; }
                    loadMaps();
                    return;
                  }
                  if (tables.length > 0) { return; }
                  loadTables();
                }}
              >
                <IconSymbol icon="refresh" size={13} />
                Refresh Data
              </Button>
            </div>
          </div>
        </Card.Section>

        <Card.Section css={{ backgroundColor: '#ffffff' }}>
          {/* Tab Navigation + Content */}
          <Box
            css={{
              padding: '4px 16px 12px',
              backgroundColor: '#ffffff',
            }}
          >
        {activeTab === 'tables' && (
          <Box id="route-tool-tables-section">
            <Box css={sectionControlBlockCss}>
              <Label
                size="md"
                color="$gray8"
                fontWeight="bold"
                css={sectionHeadingCss}
              >
                Data Tables
              </Label>

              {!tablesLoading && tables.length > 0 && (
                <Box css={sectionSelectorWrapCss}>
                  <select
                    value={selectedTableName}
                    onChange={(event) => {
                      setSelectedTableName(event.target.value);
                    }}
                    style={sectionSelectorStyle}
                  >
                    {tables.map((table) => (
                      <option key={table.name} value={table.name}>
                        {table.name}
                      </option>
                    ))}
                  </select>
                </Box>
              )}
            </Box>

            {tablesLoading && (
              <Label
                size="sm"
                color="$gray5"
                css={{ display: 'block', marginBottom: '12px' }}
              >
                Loading data tables&hellip;
              </Label>
            )}
            {!tablesLoading && tablesError && (
              <Label
                size="sm"
                color="$red7"
                css={{ display: 'block', marginBottom: '12px' }}
              >
                {tablesError}
              </Label>
            )}
            {!tablesLoading && !tablesError && tables.length === 0 && (
              <Label
                size="sm"
                color="$gray5"
                css={{ display: 'block', marginBottom: '12px' }}
              >
                No tables available yet.
              </Label>
            )}

            {selectedTable ? (
              <Box css={sectionResultWrapCss}>
                <DataTableCard
                  key={selectedTable.name}
                  table={selectedTable}
                  siteAddressesByCluster={siteAddressesByCluster}
                  baseUrl={baseUrl}
                  entityName={entityName}
                />
              </Box>
            ) : null}
          </Box>
        )}
        {activeTab === 'maps' && (
          <Box>
            <PowerBIMapViewer aeName={entityName} baseUrl={baseUrl} />
          </Box>
        )}
          </Box>
        </Card.Section>
      </Card>

      <Layout.Group
        css={{
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <Button
          variant="outline"
          onClick={() => {
            if (activeTab === 'maps') {
              navigate(`/route-tool/tables/${id}`);
              return;
            }

            const backEntity = decodeURIComponent(String(id || '')).trim();
            if (backEntity) {
              window.sessionStorage.setItem(
                UPLOAD_STATE_STORAGE_KEY,
                JSON.stringify({
                  entityName: backEntity,
                  clinicFileName: 'Already uploaded',
                  warehouseFileName: 'Already uploaded',
                  remoteUserFileName: 'Already uploaded',
                })
              );
            }

            navigate(uploadStepUrl);
          }}
        >
          <IconSymbol icon="arrow_back" size={16} />
          Back
        </Button>

        <Layout.Group css={{ marginLeft: 'auto', gap: '12px' }}>
          <Button
            variant="outline"
            css={{ color: '#196ECF', borderColor: '#196ECF' }}
            onClick={() => {
              navigate('/route-tool/search');
            }}
          >
            Close
          </Button>

          {activeTab === 'tables' && (
            <Button
              disabled={!isStep3Ready || transitioningToMaps || !mapBlobsReady}
              onClick={async () => {
                // Navigate immediately — button is only enabled once all blobs are
                // confirmed ready, and loadMaps is already running in the background.
                // Step 4 will use the sessionStorage cache if the background fetch
                // has finished, or load maps via its own useEffect.
                navigate(`/route-tool/maps/${id}`);
              }}
            >
              Next
              <IconSymbol icon="arrow_forward" size={16} />
            </Button>
          )}
        </Layout.Group>
      </Layout.Group>
    </Box>
  );
};
